import { useInboxLabelsQuery } from "@qw/graphql";
import { KnownInboxLabel } from "@qw/qw-common";
import {
  Badge,
  Button,
  ColumnList,
  ColumnListContent,
  ColumnListTrigger,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  type MaterialSymbol,
  Separator,
} from "@qw/ui";
import isEqual from "lodash/isEqual";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, type To, useLocation } from "react-router";
import { useCreateLabelForm } from "@/components/create-label-form";
import { type LabelMenuItem, labelMenuItems } from "@/constants/known-labels";
import { useInboxFilter } from "@/providers/inbox-filter-provider";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { scopedInsuredId, useInboxScope } from "@/providers/inbox-scope";
import { usePage } from "@/providers/use-page";
import { convertLabelPathsToNested, type LabelArrayItem } from "@/utils/labels";
import { FolderRow } from "./folder-row";
import { useFolderCounts } from "./use-folder-counts";

const inboxFolderItem = labelMenuItems[KnownInboxLabel.INBOX];
const primaryFolderItems = [inboxFolderItem, labelMenuItems[KnownInboxLabel.STARRED]];
const sentFolderItem = labelMenuItems[KnownInboxLabel.SENT];

const moreFolderItems = [
  labelMenuItems[KnownInboxLabel.ALL_MAIL],
  labelMenuItems[KnownInboxLabel.TRASH],
  labelMenuItems[KnownInboxLabel.SPAM],
];

// User labels are the one folder kind that never leaves the current scope, so the two rows that
// render them build their target inline against `rootScopePath` instead of going through
// `getFolderTarget` below.
const getSearchForLabel = (label: string) => `?${new URLSearchParams({ labels: label }).toString()}`;
export type FolderNavVariant = "list" | "dropdown";
type FlatLabelWithLevel = LabelArrayItem & { level: number };

interface DropdownFolderLinkProps {
  active?: boolean;
  count?: number;
  hasError?: boolean;
  icon: MaterialSymbol;
  itemKey: string;
  level?: number;
  text: string;
  to: To;
}

const flattenLabelNames = (labels: LabelArrayItem[]): string[] =>
  labels.flatMap((label) => [label.name, ...(label.children ? flattenLabelNames(label.children) : [])]);

const flattenLabelsWithLevels = (labels: LabelArrayItem[], level = 0): FlatLabelWithLevel[] =>
  labels.flatMap((label) => [
    { ...label, level },
    ...(label.children ? flattenLabelsWithLevels(label.children, level + 1) : []),
  ]);

const DropdownFolderLink = ({
  active = false,
  count,
  hasError = false,
  icon,
  itemKey,
  level = 0,
  text,
  to,
}: DropdownFolderLinkProps) => (
  <DropdownMenuItem
    key={itemKey}
    asChild
    className={cn("cursor-pointer", active && "bg-accent text-accent-foreground")}
  >
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      style={level ? { paddingLeft: `${level + 0.5}rem` } : undefined}
    >
      <Icon icon={icon} className={cn("text-base", active && "filled")} />
      <span className="min-w-0 flex-1 truncate">{text}</span>
      {count || hasError ? (
        <span className="ml-auto flex items-center gap-1">
          {count ? (
            <Badge variant="secondary" size="2xs" type="square" className="text-muted-foreground">
              {count}
            </Badge>
          ) : null}
          {hasError ? (
            <Icon
              icon="error"
              aria-label="Failed scheduled sends"
              className="size-4 text-base text-destructive filled"
            />
          ) : null}
        </span>
      ) : null}
    </Link>
  </DropdownMenuItem>
);

export const FolderNav = ({ className, variant = "list" }: { className?: string; variant?: FolderNavVariant } = {}) => {
  const { data: labelsData } = useInboxLabelsQuery();
  const { labels } = useInboxQueryStates();
  const { filters } = useInboxFilter();
  const { pathname } = useLocation();
  const { rootScopePath, rootPath } = usePage();
  const openCreateLabelForm = useCreateLabelForm();
  const scope = useInboxScope();
  // Drafts and the folder counts are insured-only: neither has an agent route to carry a scope
  // into, so an agent scope leaves both where they were rather than being threaded through them.
  const routeInsuredId = scopedInsuredId(scope);

  const nestedUserLabels = useMemo(
    () => convertLabelPathsToNested(labelsData?.inboxLabels ?? []),
    [labelsData?.inboxLabels]
  );
  const userLabelNames = useMemo(() => flattenLabelNames(nestedUserLabels), [nestedUserLabels]);
  const userLabelsWithLevels = useMemo(() => flattenLabelsWithLevels(nestedUserLabels), [nestedUserLabels]);
  const { draftsCount, getCount, hasFailedScheduledSends } = useFolderCounts(userLabelNames, routeInsuredId);

  const isDraftsActive = pathname === "/drafts" || pathname.startsWith("/drafts/");
  const draftPath = routeInsuredId ? `/drafts/${routeInsuredId}` : "/drafts";
  const activeLabels = filters.labels ?? labels;
  const isFolderActive = (item: LabelMenuItem) => !isDraftsActive && isEqual(item.labels, activeLabels);
  // Inbox is the way back to the whole mailbox, so it drops the scope from the path as well as the
  // params — every other folder is a switch *within* the current scope. The path is that scope:
  // `useClearScopeTarget` in `app/search-scope-chips.tsx` reads the search box's chip off the same
  // route segment, so keeping the segment keeps the chip. `rootPath` is always `/inbox` — one
  // `PageProvider`, see `router.tsx` — so drafts lands there too, and clearing the search needs no
  // explicit `?labels=INBOX`: with no scope the default `labels` is already `[INBOX]`.
  const getFolderTarget = (item: LabelMenuItem): To =>
    isEqual(item.labels, [KnownInboxLabel.INBOX])
      ? { pathname: rootPath, search: "" }
      : { pathname: rootScopePath, search: item.search };

  if (variant === "dropdown") {
    // Split control: the left segment links straight to Inbox and only the chevron
    // opens the switcher. Deliberately no longer names the active folder — `InboxHeader`
    // already does that in the adjacent column.
    const isInboxActive = isFolderActive(inboxFolderItem);

    return (
      <nav aria-label="Folders" className={cn("flex min-h-0 flex-col border-b border-foreground/7.5 p-1", className)}>
        <DropdownMenu>
          <div className="flex h-8 items-center overflow-hidden rounded-md">
            <Button
              asChild
              variant="ghost"
              size="sm"
              display="flex"
              className={cn(
                "h-8 min-w-0 justify-start gap-2 rounded-none px-2 text-xs",
                isInboxActive && "bg-accent text-accent-foreground"
              )}
            >
              <Link to={getFolderTarget(inboxFolderItem)} aria-current={isInboxActive ? "page" : undefined}>
                <Icon icon={inboxFolderItem.icon} className={cn("text-base", isInboxActive && "filled")} />
                <span className="truncate">{inboxFolderItem.text}</span>
              </Link>
            </Button>
            <Separator orientation="vertical" className="h-4" />
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                display="icon"
                aria-label="Switch folder"
                className="ml-0.5 h-8 rounded-md"
              >
                <Icon icon="keyboard_arrow_down" className="text-base text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
          </div>
          {/* Anchored to the chevron, which sits at the row's right edge — align end so
              the menu stays over the sidebar instead of spilling across the message list. */}
          <DropdownMenuContent align="end" side="bottom" className="w-56">
            {primaryFolderItems.map((item) => (
              <DropdownFolderLink
                key={item.text}
                itemKey={item.text}
                icon={item.icon}
                text={item.text}
                count={item.labels[0] ? getCount(item.labels[0]) : undefined}
                active={isFolderActive(item)}
                to={getFolderTarget(item)}
              />
            ))}
            <DropdownFolderLink
              itemKey="Drafts"
              icon="edit_document"
              text="Drafts"
              count={draftsCount}
              hasError={hasFailedScheduledSends}
              active={isDraftsActive}
              to={draftPath}
            />
            <DropdownFolderLink
              itemKey={sentFolderItem.text}
              icon={sentFolderItem.icon}
              text={sentFolderItem.text}
              active={isFolderActive(sentFolderItem)}
              to={getFolderTarget(sentFolderItem)}
            />
            <DropdownMenuSeparator />
            {userLabelsWithLevels.map((label) => (
              <DropdownFolderLink
                key={label.name}
                itemKey={label.name}
                icon="label"
                text={label.displayName}
                count={getCount(label.name)}
                active={!isDraftsActive && isEqual([label.name], activeLabels)}
                to={{ pathname: rootScopePath, search: getSearchForLabel(label.name) }}
                level={label.level}
              />
            ))}
            <DropdownMenuItem onSelect={openCreateLabelForm} className="cursor-pointer text-muted-foreground">
              <Icon icon="add_box" />
              Create new label
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {moreFolderItems.map((item) => (
              <DropdownFolderLink
                key={item.text}
                itemKey={item.text}
                icon={item.icon}
                text={item.text}
                active={isFolderActive(item)}
                to={getFolderTarget(item)}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    );
  }

  return (
    <nav
      aria-label="Folders"
      className={cn(
        "flex min-h-0 flex-col overflow-x-hidden overflow-y-auto border-b border-foreground/7.5 py-2",
        className
      )}
    >
      <div className="flex-none space-y-1 px-1">
        {primaryFolderItems.map((item) => (
          <FolderRow
            key={item.text}
            icon={item.icon}
            text={item.text}
            count={item.labels[0] ? getCount(item.labels[0]) : undefined}
            active={isFolderActive(item)}
            to={getFolderTarget(item)}
          />
        ))}
        <FolderRow
          icon="edit_document"
          text="Drafts"
          count={draftsCount}
          hasError={hasFailedScheduledSends}
          active={isDraftsActive}
          to={draftPath}
        />
        <FolderRow
          icon={sentFolderItem.icon}
          text={sentFolderItem.text}
          active={isFolderActive(sentFolderItem)}
          to={getFolderTarget(sentFolderItem)}
        />
      </div>

      <ColumnList
        label="Labels"
        defaultOpen={false}
        className="mt-2 flex-none border-t-0 before:ml-(--gutter) before:block before:h-px before:bg-border/35 before:content-[''] data-[state=open]:flex data-[state=open]:min-h-0 data-[state=open]:flex-1 data-[state=open]:flex-col"
      >
        <ColumnListTrigger label="Labels" />
        <ColumnListContent className="px-0 space-y-0 data-[state=open]:flex data-[state=open]:min-h-0 data-[state=open]:flex-1 data-[state=open]:flex-col data-[state=open]:pb-0">
          <ScrollableLabelRows className="min-h-0 flex-1" viewportClassName="h-full max-h-none">
            {nestedUserLabels.map((label) => (
              <LabelFolderRows
                key={label.name}
                label={label}
                labels={activeLabels}
                rootScopePath={rootScopePath}
                isDraftsActive={isDraftsActive}
                getCount={getCount}
              />
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              display="flex"
              className="h-8 justify-start px-2 text-xs text-muted-foreground"
              onClick={openCreateLabelForm}
            >
              <Icon icon="add_box" />
              Create new label
            </Button>
          </ScrollableLabelRows>
        </ColumnListContent>
      </ColumnList>

      <ColumnList
        label="More"
        defaultOpen={false}
        className="flex-none border-t-0 before:ml-(--gutter) before:block before:h-px before:bg-border/35 before:content-['']"
      >
        <ColumnListTrigger label="More" />
        <ColumnListContent>
          {moreFolderItems.map((item) => (
            <FolderRow
              key={item.text}
              icon={item.icon}
              text={item.text}
              active={isFolderActive(item)}
              to={getFolderTarget(item)}
            />
          ))}
        </ColumnListContent>
      </ColumnList>
    </nav>
  );
};

const ScrollableLabelRows = ({
  children,
  className,
  viewportClassName,
}: {
  children: ReactNode;
  className?: string;
  viewportClassName?: string;
}) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [scrollEdges, setScrollEdges] = useState({ showTop: false, showBottom: false });

  const updateScrollEdges = useCallback(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const hasOverflow = viewport.scrollHeight > viewport.clientHeight + 1;
    const nextScrollEdges = {
      showTop: hasOverflow && viewport.scrollTop > 1,
      showBottom: hasOverflow && viewport.scrollTop + viewport.clientHeight < viewport.scrollHeight - 1,
    };

    setScrollEdges((current) =>
      current.showTop === nextScrollEdges.showTop && current.showBottom === nextScrollEdges.showBottom
        ? current
        : nextScrollEdges
    );
  }, []);

  useEffect(() => {
    updateScrollEdges();

    const viewport = viewportRef.current;
    const content = contentRef.current;

    if (!viewport) {
      return;
    }

    viewport.addEventListener("scroll", updateScrollEdges, { passive: true });

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateScrollEdges);
      return () => {
        viewport.removeEventListener("scroll", updateScrollEdges);
        window.removeEventListener("resize", updateScrollEdges);
      };
    }

    const resizeObserver = new ResizeObserver(() => updateScrollEdges());
    resizeObserver.observe(viewport);

    if (content) {
      resizeObserver.observe(content);
    }

    return () => {
      viewport.removeEventListener("scroll", updateScrollEdges);
      resizeObserver.disconnect();
    };
  }, [children, updateScrollEdges]);

  return (
    <div className={cn("relative", className)}>
      <div ref={viewportRef} className={cn("max-h-64 overflow-y-auto px-1", viewportClassName)}>
        <div ref={contentRef} className="space-y-1 pb-1">
          {children}
        </div>
      </div>
      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-background to-transparent transition-opacity",
          scrollEdges.showTop ? "opacity-100" : "opacity-0"
        )}
      />
      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-background to-transparent transition-opacity",
          scrollEdges.showBottom ? "opacity-100" : "opacity-0"
        )}
      />
    </div>
  );
};

const LabelFolderRows = ({
  getCount,
  isDraftsActive,
  label,
  labels,
  level = 0,
  rootScopePath,
}: {
  getCount: (label: string) => number | undefined;
  isDraftsActive: boolean;
  label: LabelArrayItem;
  labels: string[];
  level?: number;
  rootScopePath: string;
}) => (
  <>
    <FolderRow
      icon="label"
      text={label.displayName}
      count={getCount(label.name)}
      active={!isDraftsActive && isEqual([label.name], labels)}
      to={{ pathname: rootScopePath, search: getSearchForLabel(label.name) }}
      style={level ? { marginLeft: `${level}rem` } : undefined}
    />
    {label.children?.map((child) => (
      <LabelFolderRows
        key={child.name}
        label={child}
        labels={labels}
        rootScopePath={rootScopePath}
        isDraftsActive={isDraftsActive}
        getCount={getCount}
        level={level + 1}
      />
    ))}
  </>
);
